import { LookupOption } from "../services/api";

export function isVatExemptionTruthy(value: unknown): boolean {
  if (value === false || value === 0 || value === "0") return false;
  if (value === true || value === 1 || value === "1") return true;
  const normalized = String(value ?? "").trim().toLowerCase();
  if (
    normalized === "false" ||
    normalized === "no" ||
    normalized === "n" ||
    normalized === "f"
  ) {
    return false;
  }
  return normalized === "true" || normalized === "yes" || normalized === "y";
}

export function getSupplierVatExemptionFromFormData(
  formData: Record<string, any>
): boolean {
  const selectedSources = [
    formData?.sup_id_selectedData,
    formData?.supplier_id_selectedData,
    formData?.drp_supplier_selectedData,
    formData?.drp_purchase_selectedData,
  ];

  for (const selected of selectedSources) {
    if (!selected || typeof selected !== "object") continue;
    const raw =
      (selected as Record<string, any>).vat_exemption_box ??
      (selected as Record<string, any>).vat_exemption;
    if (raw !== undefined && raw !== null && raw !== "") {
      return isVatExemptionTruthy(raw);
    }
  }

  return false;
}

export function getDefaultVatLabelForSupplier(exempt: boolean): string {
  return exempt ? "0%" : "5%";
}

export function findTaxRateLookupOption(
  options: LookupOption[] | any[],
  label: string
): any | undefined {
  if (!Array.isArray(options) || options.length === 0) return undefined;

  const normalizedLabel = String(label).trim().toLowerCase();
  const exact = options.find((opt: any) => {
    const optLabel = String(opt.label ?? opt.name ?? "").trim();
    return optLabel.toLowerCase() === normalizedLabel;
  });
  if (exact) return exact;

  const targetRate =
    normalizedLabel === "0%" || normalizedLabel === "0"
      ? 0
      : normalizedLabel === "5%" || normalizedLabel === "5"
        ? 5
        : NaN;
  if (Number.isNaN(targetRate)) return undefined;

  return options.find((opt: any) => {
    const rate = Number(opt.taxrate ?? opt.tax_rate ?? opt.rate);
    return !Number.isNaN(rate) && rate === targetRate;
  });
}

export function resolvePurchaseDefaultVatValue(
  formData: Record<string, any>,
  taxRateOptions: LookupOption[] | any[],
  staticDefault?: string
): string {
  const label = getDefaultVatLabelForSupplier(
    getSupplierVatExemptionFromFormData(formData)
  );
  const match = findTaxRateLookupOption(taxRateOptions, label);
  if (match) {
    return String(match.value ?? match.id ?? label);
  }
  return staticDefault ?? label;
}

export function isPurchaseDetailsSubmodule(
  tableName: string | undefined
): boolean {
  return String(tableName ?? "").toLowerCase() === "purchase_details";
}

export function applyPurchaseSupplierVatToRow(
  row: Record<string, any>,
  formData: Record<string, any>,
  taxRateOptions: LookupOption[] | any[]
): Record<string, any> {
  if (row.soft_delete === 1 || row.soft_delete === "1") return row;

  const label = getDefaultVatLabelForSupplier(
    getSupplierVatExemptionFromFormData(formData)
  );
  const match = findTaxRateLookupOption(taxRateOptions, label);
  const vatId = resolvePurchaseDefaultVatValue(formData, taxRateOptions, label);
  const next: Record<string, any> = {
    ...row,
    drp_vat: vatId,
    vat_id: vatId,
  };

  const desc = match?.label ?? match?.name;
  if (desc !== undefined && desc !== null && String(desc).trim() !== "") {
    next.drp_vat_desc = String(desc);
    next.vat_id_desc = String(desc);
  }

  return next;
}

export function applyPurchaseSupplierVatToRows(
  rows: Record<string, any>[],
  formData: Record<string, any>,
  taxRateOptions: LookupOption[] | any[]
): Record<string, any>[] {
  return rows.map((row) =>
    applyPurchaseSupplierVatToRow(row, formData, taxRateOptions)
  );
}
